"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselAutoPlay,
  Typography,
  InstagramReelLogo,
  type CarouselApi,
} from "@drpcrd/shared/ui";
import { FadeInSection } from "../fade-in-section";
import { WigglyArrow } from "../../icons/wiggly-arrow/wiggly-arrow";
import { CarouselNavButtons } from "../creator-video-carousel/carousel-nav-buttons";
import type { AdCarouselItem } from "./brand-collabs";

type Props = {
  items: AdCarouselItem[];
};

/**
 * Client-side carousel that renders #AD Instagram Reels cards.
 * Receives pre-fetched ad items from the server component.
 */
export const BrandCollabsCarousel = ({ items }: Props) => {
  const adAutoplayPlugin = useRef(
    CarouselAutoPlay({
      delay: 3500,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  const [adCarouselApi, setAdCarouselApi] = useState<CarouselApi>();

  return (
    <div className="flex flex-col gap-1.5">
      <div className="mr-[calc(50%-50vw)] overflow-x-clip pr-[calc(50vw-50%)] lg:mr-0 lg:pr-0">
        <Carousel
          opts={{ loop: true, align: "start" }}
          plugins={[adAutoplayPlugin.current]}
          setApi={setAdCarouselApi}
          className="w-full [&>div]:overflow-visible! lg:[&>div]:overflow-hidden!"
        >
          <CarouselContent className="-ml-2">
            {items.map((collab, index) => (
              <CarouselItem
                key={`${collab.creator}-${index}`}
                className="basis-[85%] pl-2 sm:basis-[45%] lg:basis-1/3"
              >
                <div className="space-y-2">
                  <Link
                    href={collab.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-12 items-start overflow-hidden rounded-2xl border border-neutral-500 bg-white transition-colors hover:border-neutral-900 sm:h-16"
                  >
                    <div className="relative aspect-2/3 h-full shrink-0 overflow-hidden rounded-r-2xl">
                      <Image
                        src={collab.image}
                        alt={collab.creator}
                        fill
                        sizes="(min-width: 640px) 85px, 64px"
                        className="object-cover"
                      />
                    </div>
                    <div className="flex flex-1 flex-col justify-between self-stretch p-2">
                      <Typography
                        size="sm"
                        weight="semibold"
                        color="black"
                        className="line-clamp-3"
                      >
                        {collab.creator} x {collab.brand}
                      </Typography>
                      <div className="flex items-center gap-1">
                        <InstagramReelLogo
                          width={16}
                          height={16}
                          className="shrink-0 text-neutral-900"
                        />
                        <Typography
                          size="xs"
                          color="neutral"
                          className="normal-case sm:text-sm"
                        >
                          {collab.time}
                        </Typography>
                      </div>
                    </div>
                  </Link>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
      <div className="flex items-center justify-between gap-1">
        <FadeInSection className="mt-1 flex items-end gap-2 sm:pl-4">
          <WigglyArrow
            color="currentColor"
            width={80}
            height={38}
            delay={200}
            className="mb-1 hidden text-black sm:block"
          />
          <Typography
            color="black"
            className="text-text-body-secondary text-sm sm:text-lg! sm:text-black"
          >
            Live #AD Instagram Reels
          </Typography>
        </FadeInSection>
        <div className="flex items-center gap-1">
          <CarouselNavButtons
            onPrev={() => adCarouselApi?.scrollPrev()}
            onNext={() => adCarouselApi?.scrollNext()}
          />
        </div>
      </div>
    </div>
  );
};
